import { useFormContext } from 'react-hook-form';
import FormFieldComponent from '@/components/FormFieldComponent';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useEffect, useState } from 'react';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import { REPRESENTS, SERVICE_TYPE } from '@/utils/constant';
import FormFieldSearch from '@/components/FormFieldSearch';
import { clientContactSearch, clientSearch } from '@/services/Network';
import {
  type ClientGetAllParams,
  type ClientResponse,
  type ContactGetAllParams,
  ContactResponseType,
} from '@/types/client.types';
import { fetchClient } from '@/services/clients';
import { useQuery } from '@tanstack/react-query';
import CreateUpdateClientModal from '../CreateUpdateClientModal';
import { useSearchParams } from 'react-router-dom';

export const PeClientDetails = () => {
  const { control, setValue, getValues, clearErrors } = useFormContext();
  const [openInstructions, setOpenInstructions] = useState(false);
  const [newClient, setNewClient] = useState<undefined | ClientResponse>(undefined);
  const [clientId, setClientId] = useState<string | undefined>();
  const [refreshKey, setRefreshKey] = useState(0);
  const [addNewClient, setAddNewClient] = useState(false);
  const [addNewContactFor, setAddNewContactFor] = useState<ContactResponseType | null>(null);
  const [, setSearchParam] = useSearchParams();
  const SeachIconsProps = {
    Icon: Search,
    iconProps: { style: { color: '#969696', height: '20px', width: '20px' } },
  };

  const resetValues = () => {
    setValue('clientName', null);
    setValue('contactId', null);
    setValue('case.attorneyId', null);
    setValue('case.attorneyName', null);
    clearErrors(['clientName', 'contactId', 'case.attorneyId']);
  };

  const clientData = useQuery({
    queryFn: () => fetchClient(clientId!),
    queryKey: ['client', clientId, refreshKey],
    enabled: !!clientId,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (getValues('clientId') && getValues('clientId') !== clientId) {
      setClientId(getValues('clientId') as string);
      setValue('clientId', getValues('clientId'));
    }
  }, [getValues('clientId')]);

  useEffect(() => {
    if (clientData?.data) {
      setValue('clientName', clientData.data.clientName, {
        shouldValidate: true,
      });
    }
  }, [clientData?.data]);

  useEffect(() => {
    if (newClient) {
      setClientId(newClient?.clientId);
      setValue('clientId', newClient?.clientId, {
        shouldValidate: true,
      });
      setValue('clientName', newClient?.clientName);
      setRefreshKey((prev) => prev + 1);
      resetValues();
    }
  }, [newClient]);

  useEffect(() => {
    if (!getValues('type')) {
      setValue('type', 'PROCESS_SERVICE', { shouldValidate: true });
    }
  }, []);

  return (
    <div className="flex flex-col w-full mt-11 gap-6">
      <div className="grid grid-cols-2 gap-6">
        <FormFieldSelect
          name="type"
          label="Service Type"
          required
          sortOptions
          options={SERVICE_TYPE}
          placeholder="Select Service Type"
          defaultValue="PROCESS_SERVICE"
          disabled
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldSearch<ClientGetAllParams>
          name="clientId"
          key={refreshKey}
          required
          label="Client #"
          placeholder="Search Client Code"
          props={SeachIconsProps}
          searchFieldName="code"
          api={(params, options) => {
            const updatedParams = { ...params, pageSize: 100, ...(newClient && { code: newClient?.code }) };
            return clientSearch(updatedParams, options, refreshKey);
          }}
          onChange={(e: string) => {
            if (clientId && clientId === e) return;
            resetValues();
            setClientId(e);
            setOpenInstructions(true);
          }}
          addNewOption={{
            label: 'Add New Client',
            onNewOptionClick: () => {
              setAddNewClient(true);
            },
          }}
          onSearchChange={() => {
            setNewClient(undefined);
            setClientId(undefined);
            resetValues();
          }}
        />
        <FormFieldComponent
          control={control}
          name="clientName"
          label="Client Name"
          placeholder="Client Name"
          required
          disabled
        />
      </div>

      <Collapsible
        className={`w-full bg-[#F5F5F5] flex flex-col rounded-md font-semibold  justify-between gap-2  ${openInstructions && 'border-[1px]  border-[#A41919] '}`}
        open={openInstructions}
      >
        <CollapsibleTrigger
          className={`flex justify-between items-center w-full rounded-md bg-[#F5F5F5] py-3 px-4 focus:outline-none ${!openInstructions && 'focus:border-[1px] focus:border-focuscolor'}`}
          onClick={() => setOpenInstructions(!openInstructions)}
        >
          <span>Instructions</span>
          {openInstructions ? <ChevronUp color="#969696" /> : <ChevronDown color="#969696" />}
        </CollapsibleTrigger>
        <CollapsibleContent className="bg-[#F5F5F5] p-4 rounded-md">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <span className="font-medium ">Client Instructions</span>
              <span className="font-medium text-[#676767] text-sm">
                {clientData.data?.instructions ?? 'No Instructions Available'}
              </span>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldSearch<ContactGetAllParams>
          name="contactId"
          placeholder="Search Client’s Contact"
          label="Client's Contact"
          props={SeachIconsProps}
          required
          disabled={!clientId}
          api={(params, options) => {
            const updatedParams = { ...params, pageSize: 100, clientId: clientId };
            return clientContactSearch(updatedParams, options, undefined, refreshKey);
          }}
          {...(clientId && {
            addNewOption: {
              label: 'Add New Client Contact',
              onNewOptionClick: () => {
                setSearchParam({ stepper: 'Client Details', contactMode: 'create' });
                setAddNewContactFor(ContactResponseType.STAFF);
              },
            },
          })}
        />
        <FormFieldSearch<ContactGetAllParams>
          name="case.attorneyId"
          placeholder="Search Attorney’s Name"
          label="Attorney’s Name"
          props={SeachIconsProps}
          required
          disabled={!clientId}
          api={(params, options) => {
            const updatedParams = { ...params, pageSize: 100, clientId: clientId };
            return clientContactSearch(updatedParams, options, 'LAWYER', refreshKey);
          }}
          onChange={(_value, option) => {
            const contact = option as { firstName?: string; lastName?: string } | undefined;
            const name = [contact?.firstName, contact?.lastName].filter(Boolean).join(' ');
            setValue('case.attorneyName', name || null);
          }}
          {...(clientId && {
            addNewOption: {
              label: 'Add New Client Contact',
              onNewOptionClick: () => {
                setSearchParam({ stepper: 'Client Details', contactMode: 'create' });
                setAddNewContactFor(ContactResponseType.LAWYER);
              },
            },
          })}
        />

        <FormFieldSelect
          name="process.represents"
          sortOptions
          options={REPRESENTS}
          placeholder="Select Represents"
          label="Represents"
          required
        />
      </div>
      {addNewClient && (
        <CreateUpdateClientModal
          onClose={() => setAddNewClient(false)}
          onUpdate={(client) => setNewClient(client)}
          operation="CREATE_CLIENT"
        />
      )}
      {addNewContactFor && (
        <CreateUpdateClientModal
          onClose={() => setAddNewContactFor(null)}
          onUpdate={(updatedClient) => {
            const newContact = updatedClient.contacts?.[0];
            if (addNewContactFor === ContactResponseType.STAFF && newContact) setValue('contactId', newContact.contactId);
            if (addNewContactFor === ContactResponseType.LAWYER && newContact?.type === ContactResponseType.LAWYER && newContact) {
              setValue('case.attorneyId', newContact.contactId);
              setValue(
                'case.attorneyName',
                [newContact.firstName, newContact.lastName].filter(Boolean).join(' ') || null
              );
            }
            clearErrors(['contactId', 'case.attorneyId']);
            setRefreshKey((prev) => prev + 1);
          }}
          operation="UPDATE_CONTACT"
          clientId={clientId}
        />
      )}
    </div>
  );
};
